/**
 * Parallel chunked download over HTTP Range requests.
 *
 * Kiosks pull multi-MB images (processed cutouts, composites, portraits)
 * over venue Wi-Fi. A single serial GET pays full time-to-first-byte, fills
 * the pipe with one flow-control window, and a blip restarts the whole
 * body. This helper fetches the first ~256KB with a `Range` header:
 *
 * - `206` → the `Content-Range` total + `ETag` plan the remaining chunks,
 *   fetched with bounded concurrency and retried PER CHUNK (a Wi-Fi blip
 *   re-fetches 256KB, not 9MB). `If-Range` pins every chunk to the same
 *   object version; if the server answers 200 (object replaced under us —
 *   greenscreen retakes overwrite keys), the whole download restarts once
 *   so the blob is never spliced from two versions.
 * - `200` → the server has no Range support (v0 endpoint, tiny file, or a
 *   dev proxy): degrade to reading that single stream. This makes the
 *   helper safe to ship before/independently of the v1 content backend.
 * - `416` on the FIRST chunk → zero-byte object; re-fetch without Range.
 *
 * MIRRORED COPY — the canonical, unit-tested source lives at
 * `green-screen/packages/kiosk-shared/src/fetchParallel.ts`. This app cannot
 * import across repo roots, so it carries this copy; keep them in sync.
 */

export interface FetchParallelOptions {
  /** Browser cache policy applied to every chunk, retry, and restart. */
  cache?: RequestCache;
  /** Bytes per range request. Default 262144 (256KB). */
  chunkSize?: number;
  /** Max chunk requests in flight. Default 4. */
  concurrency?: number;
  /** Extra request headers (e.g. kiosk auth) sent on every request. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  onProgress?: (loadedBytes: number, totalBytes: number | null) => void;
  /** Retries per individual chunk after its first attempt. Default 2. */
  retriesPerChunk?: number;
  /**
   * Upper bound on total requests per download. Every chunk pays full
   * route overhead (auth + DB resolution + an S3 round-trip), so past
   * this many requests the overhead dominates the transfer; tail chunks
   * grow (in multiples of chunkSize) to fit big files under the bound.
   * Default 16.
   */
  maxChunks?: number;
  /** Test seam. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

export class FetchParallelError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null = null) {
    super(message);
    this.name = 'FetchParallelError';
    this.status = status;
  }
}

class VersionChangedError extends Error {
  constructor() {
    super('object version changed mid-download');
    this.name = 'VersionChangedError';
  }
}

const DEFAULT_CHUNK_SIZE = 256 * 1024;
const DEFAULT_CONCURRENCY = 4;
const DEFAULT_MAX_CHUNKS = 16;
const DEFAULT_RETRIES_PER_CHUNK = 2;
const RETRY_BASE_DELAY_MS = 250;

interface ParsedContentRange {
  start: number;
  end: number;
  total: number | null;
}

function parseContentRange(value: string | null): ParsedContentRange | null {
  if (!value) return null;
  const match = /^bytes (\d+)-(\d+)\/(\d+|\*)$/.exec(value.trim());
  if (!match) return null;
  return {
    start: Number(match[1]),
    end: Number(match[2]),
    total: match[3] === '*' ? null : Number(match[3]),
  };
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(
        signal.reason instanceof Error
          ? signal.reason
          : new DOMException('Aborted', 'AbortError')
      );
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

async function readBodyWithProgress(
  response: Response,
  onBytes: (count: number) => void
): Promise<BlobPart> {
  const reader = response.body?.getReader?.();
  if (!reader) {
    const buffer = await response.arrayBuffer();
    onBytes(buffer.byteLength);
    return buffer;
  }
  const parts: Uint8Array[] = [];
  let size = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    size += value.byteLength;
    onBytes(value.byteLength);
  }
  const joined = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) {
    joined.set(part, offset);
    offset += part.byteLength;
  }
  return joined;
}

function blobType(response: Response): string {
  return response.headers.get('content-type') ?? 'application/octet-stream';
}

async function fetchWholeObject(
  url: string,
  options: FetchParallelOptions
): Promise<Blob> {
  const fetchImpl = options.fetchImpl ?? fetch;
  if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  // cache carries over: the fallback fires while the object is churning,
  // which is exactly when a cached copy is most likely stale.
  const response = await fetchImpl(url, {
    cache: options.cache,
    headers: options.headers ?? {},
    signal: options.signal,
  });
  if (!response.ok) {
    throw new FetchParallelError(
      `HTTP ${response.status} for ${url}`,
      response.status
    );
  }
  const lengthHeader = response.headers.get('content-length');
  const total = lengthHeader ? Number(lengthHeader) : null;
  let loaded = 0;
  const body = await readBodyWithProgress(response, (count) => {
    loaded += count;
    options.onProgress?.(loaded, total);
  });
  return new Blob([body], { type: blobType(response) });
}

async function runFetchParallel(
  url: string,
  options: FetchParallelOptions
): Promise<Blob> {
  const chunkSize = options.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
  const retriesPerChunk = options.retriesPerChunk ?? DEFAULT_RETRIES_PER_CHUNK;
  const maxChunks = Math.max(2, options.maxChunks ?? DEFAULT_MAX_CHUNKS);
  const baseHeaders = options.headers ?? {};
  const onProgress = options.onProgress;
  const fetchImpl = options.fetchImpl ?? fetch;

  // One internal controller fans the caller's abort out to every in-flight
  // chunk, and lets a failed worker cancel its siblings.
  const controller = new AbortController();
  const onCallerAbort = () => controller.abort();
  if (options.signal) {
    if (options.signal.aborted) throw new DOMException('Aborted', 'AbortError');
    options.signal.addEventListener('abort', onCallerAbort, { once: true });
  }
  const requestInit = (headers: Record<string, string>): RequestInit => ({
    cache: options.cache,
    headers,
    signal: controller.signal,
  });

  let loaded = 0;
  const addLoaded = (total: number | null) => (count: number) => {
    loaded += count;
    onProgress?.(loaded, total);
  };

  try {
    const first = await fetchImpl(
      url,
      requestInit({
        ...baseHeaders,
        Range: `bytes=0-${chunkSize - 1}`,
      })
    );

    if (first.status === 416) {
      // Range on a zero-byte object is unsatisfiable by definition.
      await first.body?.cancel();
      const empty = await fetchImpl(url, requestInit(baseHeaders));
      if (!empty.ok)
        throw new FetchParallelError(
          `HTTP ${empty.status} for ${url}`,
          empty.status
        );
      return new Blob([await readBodyWithProgress(empty, addLoaded(null))], {
        type: blobType(empty),
      });
    }

    if (first.status === 200) {
      // No Range support: single-stream degradation, exactly today's behavior.
      const lengthHeader = first.headers.get('content-length');
      const total = lengthHeader ? Number(lengthHeader) : null;
      return new Blob([await readBodyWithProgress(first, addLoaded(total))], {
        type: blobType(first),
      });
    }

    if (first.status !== 206) {
      throw new FetchParallelError(
        `HTTP ${first.status} for ${url}`,
        first.status
      );
    }

    const contentRange = parseContentRange(first.headers.get('content-range'));
    const type = blobType(first);
    if (!contentRange || contentRange.total === null) {
      // A 206 without a usable total can't be planned into chunks;
      // re-fetch whole rather than guessing.
      await first.body?.cancel();
      const whole = await fetchImpl(url, requestInit(baseHeaders));
      if (!whole.ok)
        throw new FetchParallelError(
          `HTTP ${whole.status} for ${url}`,
          whole.status
        );
      return new Blob([await readBodyWithProgress(whole, addLoaded(null))], {
        type: blobType(whole),
      });
    }

    const total = contentRange.total;
    const etag = first.headers.get('etag');
    const progress = addLoaded(total);
    const firstPart = await readBodyWithProgress(first, progress);
    const remaining = total - chunkSize;
    if (remaining <= 0) {
      return new Blob([firstPart], { type });
    }

    // Adaptive tail planning: small files keep fine-grained chunkSize
    // pieces (cheap per-chunk retry); big files grow the tail chunk so
    // the whole download fits under maxChunks requests.
    const tailChunkSize =
      chunkSize *
      Math.max(1, Math.ceil(remaining / (maxChunks - 1) / chunkSize));
    const tailCount = Math.ceil(remaining / tailChunkSize);

    const parts: BlobPart[] = new Array<BlobPart>(1 + tailCount);
    parts[0] = firstPart;

    const fetchTailChunk = async (tailIndex: number): Promise<BlobPart> => {
      const start = chunkSize + tailIndex * tailChunkSize;
      const end = Math.min(start + tailChunkSize - 1, total - 1);
      const headers: Record<string, string> = {
        ...baseHeaders,
        Range: `bytes=${start}-${end}`,
      };
      if (etag) headers['If-Range'] = etag;

      for (let attempt = 0; ; attempt++) {
        try {
          const response = await fetchImpl(url, requestInit(headers));
          if (response.status === 200) {
            // If-Range mismatch: the object was replaced. The 200 body is
            // the NEW whole file — discard it and restart the download.
            await response.body?.cancel();
            throw new VersionChangedError();
          }
          if (response.status !== 206) {
            throw new FetchParallelError(
              `chunk ${tailIndex} of ${url}: HTTP ${response.status}`,
              response.status
            );
          }
          return await readBodyWithProgress(response, progress);
        } catch (error) {
          if (error instanceof VersionChangedError) throw error;
          if (controller.signal.aborted) throw error;
          if (attempt >= retriesPerChunk) throw error;
          await abortableDelay(
            RETRY_BASE_DELAY_MS * (attempt + 1),
            controller.signal
          );
        }
      }
    };

    let nextTail = 0;
    const workers = Array.from(
      { length: Math.min(concurrency, tailCount) },
      async () => {
        for (;;) {
          const tailIndex = nextTail++;
          if (tailIndex >= tailCount) return;
          parts[1 + tailIndex] = await fetchTailChunk(tailIndex);
        }
      }
    );

    try {
      await Promise.all(workers);
    } catch (error) {
      controller.abort();
      throw error;
    }

    return new Blob(parts, { type });
  } finally {
    options.signal?.removeEventListener('abort', onCallerAbort);
  }
}

/**
 * Download `url` as a Blob using parallel ~256KB Range requests, degrading
 * to a plain single-stream GET against servers without Range support.
 */
export async function fetchParallelBlob(
  url: string,
  options: FetchParallelOptions = {}
): Promise<Blob> {
  try {
    return await runFetchParallel(url, options);
  } catch (error) {
    if (error instanceof VersionChangedError) {
      // The object changed under us once (e.g. a retake overwrote the
      // key). Restart against the new version; a second flip mid-restart
      // means the URL is churning and the caller should surface it.
      try {
        return await runFetchParallel(url, options);
      } catch (retryError) {
        if (retryError instanceof VersionChangedError) {
          // Still churning — e.g. the upscale pipeline replacing the
          // object while we download. A plain GET with no Range/If-Range
          // reads ONE consistent snapshot: slower, but the customer gets
          // the file instead of a "kept changing" failure
          // (KIOSK-FRONTEND-112).
          return await fetchWholeObject(url, options);
        }
        throw retryError;
      }
    }
    throw error;
  }
}
